// src/pages/student/ResearchWorkUpload.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Chip,
    Grid,
    Slide,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Tabs,
    Tab,
    Avatar,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Zoom,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    LinearProgress,
    Badge,
    useMediaQuery,
    Autocomplete,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Description,
    HealthAndSafety,
    MenuBook,
    Article,
    Storage,
    Attachment,
    CloudUpload,
    Send,
    Visibility,
    Feedback,
    CheckCircle,
    Schedule,
    Download,
    Warning,
    Category,
    SupervisorAccount,
    TaskAlt,
    Pending,
    ExpandMore,
    DateRange,
    RateReview
} from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';

import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchWorkStore,
    APIResearchWorkUpdate,
    APIResearchWorkSubmitReview,
    APIResearchWorkDelete,
    APIResearchWorkListByStudent,
    APISpecialtyByInstitution,
    APIAssignStudentByStudent,
    APISupervisorProfileImage
} from '../../apiServices/APIs';
import { 
    getUserDetails,
    getImageUrl,
    formatCreatedAtDate
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const getInitials = (name) => {
    if (!name) return 'N/A';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const getSupervisorName = (work) => {
    if (!work) return '';
    if (work.latest_review && work.latest_review.supervisor_name) {
        return work.latest_review.supervisor_name;
    }
    return work.supervisor_name || work.supervisor?.full_name_with_title || work.supervisor?.name || '';
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ResearchWorkUpload = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [works, setWorks] = useState([]);
    const [supervisorsData, setSupervisors] = useState([]);
    const [specialtiesData, setSpecialtiesData] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [editingWork, setEditingWork] = useState(null);
    const [selectedWork, setSelectedWork] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [specialtySearchInput, setSpecialtySearchInput] = useState('');
    const [supervisorSearchInput, setSupervisorSearchInput] = useState('');
    const isMounted = useRef(true);
    const studentId = getUserDetails("Id");

    const [formData, setFormData] = useState({
        work_type: 'chapter',
        specialty_id: '',
        specialty_name: '',
        supervisor_id: '',
        supervisor_name: '',
        title: '',
        description: '',
        word_count: ''
    });

    const workTypes = [
        { value: 'proposal', label: 'Research Proposal', icon: <Description />, color: 'primary' },
        { value: 'ethics_application', label: 'Ethics Application', icon: <HealthAndSafety />, color: 'secondary' },
        { value: 'chapter', label: 'Thesis Chapter', icon: <MenuBook />, color: 'info' },
        { value: 'full_thesis', label: 'Full Thesis', icon: <Article />, color: 'success' },
        { value: 'dataset', label: 'Dataset', icon: <Storage />, color: 'warning' },
        { value: 'presentation', label: 'Presentation', icon: <Description />, color: 'error' },
        { value: 'poster', label: 'Poster', icon: <Description />, color: 'default' },
        { value: 'supplementary_material', label: 'Supplementary Material', icon: <Attachment />, color: 'info' }
    ];

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        fetchSupervisors();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);

        try {
            await Promise.all([
                fetchWorks(),
                fetchSpecialtiesData()
            ]);
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchWorks = async () => {
        const result = await apiService.listById(APIResearchWorkListByStudent, studentId);
        if (result.success && isMounted.current) {
            setWorks(Array.isArray(result.data) ? result.data : []);
        }
    };
    
    const fetchSupervisors = async () => {
        try {
            const result = await apiService.listById(APIAssignStudentByStudent, studentId);
            if (result.success && isMounted.current) {
                const assignmentsData = Array.isArray(result.data) ? result.data : [];
                
                const supervisors = assignmentsData.map(assignment => {
                    const supervisor = assignment.supervisor || assignment;
                    
                    return {
                        id: supervisor.id,
                        name: supervisor.full_name_with_title || supervisor.full_name || supervisor.name || 'Unnamed Supervisor',
                        profile_image: supervisor.profile_image_path || supervisor.profile_image || '',
                    };
                }).filter(s => s.id);
                
                setSupervisors(supervisors);
            } else {
                console.error("Failed to fetch assignments:", result?.message);
                showToast("Error", "Failed to load supervisors", "error");
            }
        } catch (error) {
            console.error("Error fetching assignments:", error);
            showToast("Error", "Failed to load supervisors", "error");
        }
    };

    const fetchSpecialtiesData = async () => {
        try {
            const result = await apiService.listByInstitutionAndId(APISpecialtyByInstitution);
            if (result.success && isMounted.current) {
                const specialties = Array.isArray(result.data) ? result.data.reverse() : [];
                setSpecialtiesData(specialties);
            } else {
                console.error("Failed to load specialties:", result.message);
            }
        } catch (error) {
            console.error("Error fetching specialties:", error);
            showToast("Error", "Failed to load specialties", "error");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSpecialtySelect = (event, selectedSpecialty) => {
        if (selectedSpecialty) {
            setFormData(prev => ({
                ...prev,
                specialty_id: selectedSpecialty.id,
                specialty_name: selectedSpecialty.name
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                specialty_id: '',
                specialty_name: ''
            }));
        }
    };

    const handleSupervisorSelect = (event, selectedSupervisor) => {
        if (selectedSupervisor) {
            setFormData(prev => ({
                ...prev,
                supervisor_id: selectedSupervisor.id,
                supervisor_name: selectedSupervisor.name
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                supervisor_id: '',
                supervisor_name: ''
            }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const extension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['doc', 'docx'];
            if (allowedExtensions.includes(extension)) {
                setUploadFile(file);
            } else {
                showToast("Error", "Please upload a Word document (.doc or .docx) only", "error");
            }
        }
    };

    const handleOpenAdd = () => {
        setEditingWork(null);
        setFormData({
            work_type: 'chapter',
            specialty_id: '',
            specialty_name: '',
            supervisor_id: '',
            supervisor_name: '',
            title: '',
            description: '',
            word_count: ''
        });
        setUploadFile(null);
        setSpecialtySearchInput('');
        setSupervisorSearchInput('');
        setOpenDialog(true);
    };

    const handleOpenEdit = (work) => {
        setEditingWork(work);
        setFormData({
            work_type: work.work_type,
            specialty_id: work.specialty_id || '',
            specialty_name: work.specialty_name || '',
            supervisor_id: work.supervisor_id || '',
            supervisor_name: getSupervisorName(work),
            title: work.title,
            description: work.description || '',
            word_count: work.word_count || ''
        });
        setUploadFile(null);
        setSpecialtySearchInput(work.specialty_name || '');
        setSupervisorSearchInput(getSupervisorName(work));
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingWork(null);
        setUploadFile(null);
    };

    const handleOpenViewDialog = (work) => {
        setSelectedWork(work);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedWork(null);
    };

    const handleSave = async () => {
        if (!formData.title) {
            showToast("Error", "Title is required", "error");
            return;
        }

        if (!formData.specialty_id) {
            showToast("Error", "Please select a research specialty", "error");
            return;
        }

        if (!formData.supervisor_id) {
            showToast("Error", "Please select a supervisor", "error");
            return;
        }

        if (!editingWork && !uploadFile) {
            showToast("Error", "Please select a file to upload", "error");
            return;
        }

        setIsSubmitting(true);
        const formDataToSend = new FormData();
        formDataToSend.append('student_id', studentId);
        formDataToSend.append('university_id', getUserDetails("universityId"));
        formDataToSend.append('work_type', formData.work_type);
        formDataToSend.append('specialty_id', formData.specialty_id);
        formDataToSend.append('supervisor_id', formData.supervisor_id);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        if (formData.word_count) formDataToSend.append('word_count', formData.word_count);
        if (uploadFile) formDataToSend.append('file', uploadFile);
        
        if (editingWork) {
            formDataToSend.append('updateId', editingWork.id);
        }

        try {
            let result;
            if (editingWork) {
                result = await apiService.update(APIResearchWorkUpdate, formDataToSend);
            } else {
                result = await apiService.create(APIResearchWorkStore, formDataToSend);
            }
            
            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseDialog();
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to save research work", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitForReview = async (work) => {
        const formData = new FormData();
        formData.append('updateId', work.id);
        
        try {
            const result = await apiService.update(APIResearchWorkSubmitReview, formData);
            if (result && result.success) {
                showToast("Success", "Work submitted for review", "success");
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", "Failed to submit for review", "error");
        }
        handleMenuClose();
    };

    const handleDelete = async (work) => {
        const formData = new FormData();
        formData.append('updateId', work.id);
        
        try {
            const result = await apiService.delete(APIResearchWorkDelete, formData);
            if (result && result.success) {
                showToast("Success", "Research work deleted successfully", "success");
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", "Failed to delete research work", "error");
        }
        handleMenuClose();
    };

    const handleDownload = (work) => {
        if (work.file_url) {
            window.open(work.file_url, '_blank');
        }
    };

    const handleMenuOpen = (event, work) => {
        setSelectedWork(work);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedWork(null);
    };

    const getWorkTypeIcon = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.icon || <Description />;
    };

    const getWorkTypeColor = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.color || 'default';
    };

    const getWorkTypeLabel = (type) => {
        const found = workTypes.find(t => t.value === type);
        return found?.label || type;
    };

    const filteredWorks = activeTab === 0 
        ? works 
        : activeTab === 4 
            ? works.filter(w => w.work_status === 'completed')
            : works.filter(w => w.work_status === activeTab);

    const getSupervisorAvatarUrl = (profileImage) => {
        if (profileImage) {
            return getImageUrl(APISupervisorProfileImage, profileImage);
        }
        return null;
    };

    if (loading) {
        return <LoadingState message="Loading Research Works..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Research Works" variant="research" fullPage retryText="Retry Loading" />;
    }

    const statusTabs = [
        { value: 0, label: 'All Uploads', count: works.length, icon: <Description /> },
        { value: 'submitted', label: 'Submitted', count: works.filter(w => w.work_status === 'submitted' || w.work_status === 'under_review').length, icon: <Schedule /> },
        { value: 'revision_requested', label: 'Revision Needed', count: works.filter(w => w.work_status === 'revision_requested').length, icon: <Pending /> },
        { value: 'approved', label: 'Approved', count: works.filter(w => w.work_status === 'approved').length, icon: <CheckCircle /> },
        { value: 4, label: 'Completed', count: works.filter(w => w.work_status === 'completed').length, icon: <TaskAlt /> }
    ];

    return (
        <>
            <FullWidthContainer >
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <StudentBar notificationCount={9} />
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Description /> Research Work Upload
                            </Typography>

                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenAdd}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    {isMobile ? 'Upload' : 'Upload Work'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchAllData}
                                    sx={{ 
                                        textTransform: 'none', 
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        {statusTabs.map(tab => (
                            <Tab 
                                key={tab.value}
                                icon={tab.icon}
                                label={
                                    <Badge badgeContent={tab.count} color="primary" sx={{ mr: 1 }}>
                                        {tab.label}
                                    </Badge>
                                } 
                                value={tab.value}
                            />
                        ))}
                    </Tabs>
                </Paper>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    {filteredWorks.length === 0 ? (
                        <Paper sx={{ 
                            p: { xs: 4, sm: 8 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <Description sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No research work uploaded yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Click "Upload Work" to submit your research documents
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredWorks.map((work, index) => {
                                const supervisorName = getSupervisorName(work);

                                return (
                                    <Grid item xs={12} sm={6} lg={4} key={work.id}>
                                        <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                            <Card sx={{ 
                                                height: '100%', 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: theme.shadows[8]
                                                }
                                            }}>
                                                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between', 
                                                        alignItems: 'flex-start', 
                                                        mb: 2,
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        gap: { xs: 1, sm: 0 }
                                                    }}>
                                                        <Chip
                                                            icon={getWorkTypeIcon(work.work_type)}
                                                            label={work.formatted_work_type}
                                                            size="small"
                                                            color={getWorkTypeColor(work.work_type)}
                                                            variant="outlined"
                                                        />
                                                        <Chip
                                                            icon={work.work_status_icon === 'Cancel' ? <CancelIcon /> : work.work_status_icon === 'CheckCircle' ? <CheckCircle /> : <Schedule />}
                                                            label={work.formatted_work_status}
                                                            size="small"
                                                            color={work.work_status_color}
                                                        />
                                                    </Box>
                                                    
                                                    <Typography variant="h6" sx={{ 
                                                        fontWeight: 'bold', 
                                                        mb: 1, 
                                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                                    }}>
                                                        {work.title}
                                                    </Typography>
                                                    
                                                    {work.specialty_name && (
                                                        <Chip 
                                                            icon={<Category />}
                                                            label={work.specialty_name}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ mb: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                        />
                                                    )}
                                                    
                                                    {supervisorName && (
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                p: 1.5,
                                                                mb: 1.5,
                                                                borderRadius: 2,
                                                                bgcolor: theme.palette.primary.light + '10',
                                                                border: `1px solid ${theme.palette.primary.light}`,
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleOpenViewDialog(work)}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Avatar 
                                                                    sx={{ 
                                                                        width: 45, 
                                                                        height: 45,
                                                                        bgcolor: stringToColor(supervisorName),
                                                                        border: `2px solid ${theme.palette.primary.main}`
                                                                    }}
                                                                >
                                                                    {getInitials(supervisorName)}
                                                                </Avatar>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.primary.dark }}>
                                                                        <SupervisorAccount fontSize="small" />
                                                                        {supervisorName}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Paper>
                                                    )}
                                                    
                                                    <Typography variant="body2" color="text.secondary" sx={{ 
                                                        mb: 1,
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                    }}>
                                                        {work.description?.length > (isMobile ? 80 : 100) 
                                                            ? work.description.substring(0, isMobile ? 80 : 100) + '...' 
                                                            : work.description}
                                                    </Typography>
                                                    
                                                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Version: v{work.version_number}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                            Size: {work.file_size_mb} MB
                                                        </Typography>
                                                        {work.word_count && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                                Words: {work.word_count.toLocaleString()}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    
                                                    {work.plagiarism_score && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                                Plagiarism Score: {work.plagiarism_score}%
                                                            </Typography>
                                                            <LinearProgress 
                                                                variant="determinate" 
                                                                value={work.plagiarism_score}
                                                                color={work.plagiarism_score > 30 ? 'error' : work.plagiarism_score > 15 ? 'warning' : 'success'}
                                                                sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                                                            />
                                                        </Box>
                                                    )}
                                                    
                                                    {work.revision_feedback && (
                                                        <Alert severity="warning" sx={{ mt: 2, p: 1 }}>
                                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                {work.revision_feedback}
                                                            </Typography>
                                                        </Alert>
                                                    )}
                                                </CardContent>
                                                
                                                <CardActions sx={{ 
                                                    p: 2, 
                                                    pt: 0, 
                                                    justifyContent: 'space-between',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: { xs: 1, sm: 0 }
                                                }}>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton size="small" onClick={() => handleOpenViewDialog(work)} color="info">
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Download">
                                                            <IconButton size="small" onClick={() => handleDownload(work)} color="secondary">
                                                                <Download fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {/* Green Color Button for Supervisor Revision */}
                                                        {work.revision_file_url && (
                                                            <Tooltip title="Download Supervisor Revision">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => window.open(work.revision_file_url, '_blank')} 
                                                                    color="success"
                                                                >
                                                                    <Download fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {work.plagiarism_report_url && (
                                                            <Tooltip title="Plagiarism Report">
                                                                <IconButton size="small" href={work.plagiarism_report_url} target="_blank">
                                                                    <Warning fontSize="small" color="warning" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    {work.work_status !== 'completed' && work.work_status !== 'approved' && (
                                                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, work)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </CardActions>
                                            </Card>
                                        </Zoom>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </motion.div>

                {/* Actions Menu */}
                <Menu 
                    anchorEl={anchorEl} 
                    open={Boolean(anchorEl)} 
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            minWidth: 150
                        }
                    }}
                >
                    <MenuItem onClick={() => { handleOpenEdit(selectedWork); handleMenuClose(); }}>
                        <EditIcon sx={{ mr: 1 }} /> Edit
                    </MenuItem>
                    <MenuItem onClick={() => handleDownload(selectedWork)}>
                        <Download sx={{ mr: 1 }} /> Download Original
                    </MenuItem>
                    {/* Supervisor Revision Menu Action */}
                    {selectedWork?.revision_file_url && (
                        <MenuItem onClick={() => { window.open(selectedWork.revision_file_url, '_blank'); handleMenuClose(); }} sx={{ color: 'success.main' }}>
                            <Download sx={{ mr: 1 }} /> Download Supervisor Revision
                        </MenuItem>
                    )}
                    {selectedWork?.work_status !== 'approved' && selectedWork?.work_status !== 'submitted' && (
                        <MenuItem onClick={() => { handleSubmitForReview(selectedWork); handleMenuClose(); }}>
                            <Send sx={{ mr: 1 }} /> Submit for Review
                        </MenuItem>
                    )}
                    <MenuItem onClick={() => handleDelete(selectedWork)} sx={{ color: 'error.main' }}>
                        <DeleteIcon sx={{ mr: 1 }} /> Delete
                    </MenuItem>
                </Menu>

                {/* View Details Dialog */}
                <Dialog
                    open={openViewDialog}
                    onClose={handleCloseViewDialog}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Research Work Details
                        </Typography>
                        <IconButton
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                            onClick={handleCloseViewDialog}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        {selectedWork && (
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                                        {selectedWork.upload_id}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            icon={getWorkTypeIcon(selectedWork.work_type)}
                                            label={getWorkTypeLabel(selectedWork.work_type)}
                                            color={getWorkTypeColor(selectedWork.work_type)}
                                            variant="outlined"
                                        />
                                        <Chip
                                            icon={selectedWork.work_status_icon === 'Cancel' ? <CancelIcon /> : selectedWork.work_status_icon === 'CheckCircle' ? <CheckCircle /> : <Schedule />}
                                            label={selectedWork.formatted_work_status}
                                            color={selectedWork.work_status_color}
                                        />
                                    </Box>
                                </Box>

                                {/* Work Information */}
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Work Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Title:</strong> {selectedWork.title}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Description:</strong> {selectedWork.description || 'Not provided'}
                                                </Typography>
                                            </Grid>
                                            {selectedWork.specialty_name && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Research Specialty:</strong>
                                                        <Chip 
                                                            icon={<Category />}
                                                            label={selectedWork.specialty_name}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </Typography>
                                                </Grid>
                                            )}
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <strong>Version:</strong> v{selectedWork.version_number}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <strong>File Size:</strong> {selectedWork.file_size_mb} MB
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <strong>Word Count:</strong> {selectedWork.word_count?.toLocaleString() || 'Not specified'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Supervisor Information */}
                                {getSupervisorName(selectedWork) && (
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                <SupervisorAccount sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                Supervisor
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="subtitle2">
                                                <strong>Name:</strong> {getSupervisorName(selectedWork)}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Plagiarism Information */}
                                {selectedWork.plagiarism_score && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                Plagiarism Information
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Plagiarism Score:</strong> {selectedWork.plagiarism_score}%
                                                    </Typography>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={selectedWork.plagiarism_score}
                                                        color={selectedWork.plagiarism_score > 30 ? 'error' : selectedWork.plagiarism_score > 15 ? 'warning' : 'success'}
                                                        sx={{ height: 8, borderRadius: 4, mt: 1 }}
                                                    />
                                                </Grid>
                                                {selectedWork.plagiarism_report_url && (
                                                    <Grid item xs={12}>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<Download />}
                                                            href={selectedWork.plagiarism_report_url}
                                                            target="_blank"
                                                            size="small"
                                                        >
                                                            Download Plagiarism Report
                                                        </Button>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Review Information */}
                                {selectedWork.latest_review && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                <RateReview sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                Review Information
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Reviewer:</strong> {selectedWork.latest_review.supervisor_name}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Review Comments:</strong>
                                                    </Typography>
                                                    <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], mt: 1 }}>
                                                        <Typography variant="body2">
                                                            {selectedWork.latest_review.review_comments}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                                {selectedWork.latest_review.score && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="subtitle2">
                                                            <strong>Score:</strong> {selectedWork.latest_review.score}/100
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Review Date:</strong> {formatCreatedAtDate(selectedWork.latest_review.review_submitted_at)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Revision Feedback */}
                                {selectedWork.revision_feedback && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                <Feedback sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                Revision Feedback
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Alert severity="warning" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {selectedWork.revision_feedback}
                                            </Alert>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Dates */}
                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Important Dates
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <strong>Uploaded:</strong> {formatCreatedAtDate(selectedWork.uploaded_at)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <strong>Last Modified:</strong> {formatCreatedAtDate(selectedWork.last_modified)}
                                                </Typography>
                                            </Grid>
                                            {selectedWork.submitted_at && (
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Submitted:</strong> {formatCreatedAtDate(selectedWork.submitted_at)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {selectedWork.review_deadline && (
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2">
                                                        <strong>Review Deadline:</strong> {formatCreatedAtDate(selectedWork.review_deadline)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Download Section */}
                                {selectedWork.file_url && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<Download />}
                                            onClick={() => handleDownload(selectedWork)}
                                            fullWidth
                                            sx={{ mb: 1 }}
                                        >
                                            Download Research Work (Word)
                                        </Button>
                                    </Box>
                                )}

                                {/* Supervisor Revision Download Section */}
                                {selectedWork.revision_file_url && (
                                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<Download />}
                                            onClick={() => window.open(selectedWork.revision_file_url, '_blank')}
                                            fullWidth
                                        >
                                            Download Supervisor Revision
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={handleCloseViewDialog} variant="outlined">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseDialog();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    scroll="paper"
                    disableEscapeKeyDown
                >
                    <DialogTitle sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}>
                        <b>{editingWork ? 'Edit Research Work' : 'Upload New Research Work'}</b>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ p: { xs: 1, sm: 2 } }}>
                            {/* Work Type Selection */}
                            <FormControl fullWidth size={isMobile ? "small" : "medium"} sx={{ mb: 2 }}>
                                <InputLabel>Work Type *</InputLabel>
                                <Select
                                    name="work_type"
                                    value={formData.work_type}
                                    onChange={handleInputChange}
                                    label="Work Type *"
                                >
                                    {workTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {type.icon}
                                                {type.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            {/* Specialty Selection */}
                            <Autocomplete
                                options={specialtiesData}
                                loading={loading}
                                value={specialtiesData.find(s => s.id === formData.specialty_id) || null}
                                onChange={handleSpecialtySelect}
                                inputValue={specialtySearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setSpecialtySearchInput(newInputValue);
                                }}
                                getOptionLabel={(option) => `${option.name} ${option.specialty_code ? `(${option.specialty_code})` : ''}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Research Specialty *"
                                        required
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Category color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            
                            {/* Supervisor Selection */}
                            <Autocomplete
                                options={supervisorsData}
                                loading={loading}
                                value={supervisorsData.find(s => s.id === formData.supervisor_id) || null}
                                onChange={handleSupervisorSelect}
                                inputValue={supervisorSearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setSupervisorSearchInput(newInputValue);
                                }}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar 
                                                src={getSupervisorAvatarUrl(option.profile_image)}
                                                sx={{ width: 40, height: 40 }}
                                            >
                                                {!option.profile_image && getInitials(option.name)}
                                            </Avatar>
                                            <Typography variant="body1">
                                                <strong>{option.name}</strong>
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Supervisor *"
                                        required
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SupervisorAccount color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            
                            {/* Title */}
                            <TextField
                                fullWidth
                                label="Title *"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                            />
                            
                            {/* Description */}
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                multiline
                                rows={3}
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                            />
                            
                            {/* Word Count */}
                            <TextField
                                fullWidth
                                label="Word Count (Optional)"
                                type="number"
                                name="word_count"
                                value={formData.word_count}
                                onChange={handleInputChange}
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                            />
                            
                            {/* File Upload */}
                            <Button
                                fullWidth
                                variant="outlined"
                                component="label"
                                startIcon={<CloudUpload />}
                                sx={{ mb: 1 }}
                                size={isMobile ? "small" : "medium"}
                            >
                                {editingWork ? 'Upload New Version (Optional)' : 'Select Word Document (.doc, .docx) *'}
                                <input
                                    type="file"
                                    hidden
                                    accept=".doc,.docx"
                                    onChange={handleFileChange}
                                />
                            </Button>
                            {uploadFile && (
                                <Chip
                                    label={uploadFile.name}
                                    onDelete={() => setUploadFile(null)}
                                    color="primary"
                                    size="small"
                                />
                            )}
                            {editingWork?.file_url && !uploadFile && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                                    Current file: <a href={editingWork.file_url} target="_blank" rel="noopener noreferrer">Download</a>
                                </Typography>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button 
                            color="error" 
                            onClick={handleCloseDialog} 
                            variant="outlined" 
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={handleSave} 
                            variant="contained"
                            disabled={isSubmitting || (!editingWork && !uploadFile) || !formData.title || !formData.specialty_id || !formData.supervisor_id}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <CloudUpload />}
                        >
                            {isSubmitting ? 'Saving...' : (editingWork ? 'Update' : 'Upload')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default ResearchWorkUpload;